import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import dayjs from "dayjs";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import List from "../../common/list";
import DropdownButton from "../../common/dropDownButton";
import { TextInput } from "../../common/fields";
import { useListSearch } from "../../common/hooks";
import * as AuditLogConstants from "../../../constants/AuditLogConstants";
import { earliestDay, initials, retentionPeriod } from "../../../helpers/AuditLogHelper";
import {
  auditLogClearFilters,
  auditLogDetailsClose,
  auditLogDetailsRequest,
  auditLogExportRequest,
  auditLogListRequest,
  auditLogOptionsRequest,
  auditLogSetQuery,
} from "../../../store/slices/auditLogSlice";
import { ActionTag } from "./AuditLogTags";
import AuditLogDetails from "./AuditLogDetails";
import AuditLogPeoplePicker from "./AuditLogPeoplePicker";
import "./audit-logs.scss";

// The "all" choice of a filter. PrimeReact treats an empty value ("", null) as
// nothing chosen and shows the placeholder, so the choice needs a value of its own.
export const ALL = "__all__";

const When = (row) => (
  <div className="audit-when">
    {dayjs(row.createdAt).format("DD MMM YYYY")}
    <br />
    {dayjs(row.createdAt).format("hh:mm a")}
  </div>
);

const Who = (row) => (
  <div className="audit-who">
    <span className={`audit-avatar ${row.actor?.isSystem ? "audit-avatar-system" : ""}`} aria-hidden="true">
      {row.actor?.isSystem ? <i className="pi pi-cog" /> : initials(row.actor?.name)}
    </span>
    <span>
      <b>{row.actor?.name}</b>
      <span className="audit-muted">{row.actor?.isSystem ? row.actor?.source : row.actor?.email}</span>
    </span>
  </div>
);

const Action = (row) => <ActionTag action={row.action} />;

// The record's name, and its grey second line: an email, "to the cohort Sales Team".
// The name is a real button, so a keyboard user can open the details the row
// opens for a mouse user.
const Record = (row, openDetails, title, fallbackText) => (
  <div>
    <button
      type="button"
      className="audit-record"
      data-audit-id={row.id}
      aria-label={title}
      onClick={(e) => {
        // The row's own click would open the same details a second time.
        e.stopPropagation();
        openDetails(row.id);
      }}
    >
      {row.recordName || fallbackText}
    </button>
    {row.recordDetail && <span className="audit-muted">{row.recordDetail}</span>}
  </div>
);

// What a row is called to a screen reader: "Completed: Information Security Policy 2026".
const detailsTitle = (row) => (row.recordName ? `${row.action}: ${row.recordName}` : row.action);

const WhatChanged = (row) => <span className="audit-summary">{row.summary}</span>;

export const AuditLogList = ({
  labels,
  query,
  list,
  totalCount,
  requesting,
  loaded,
  retentionDays,
  exporting,
  options,
  details,
  detailsRequesting,
  listRequest,
  setQuery,
  clearFilters,
  optionsRequest,
  detailsRequest,
  detailsClose,
  exportRequest,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const t = (id) => intl.formatMessage({ id });
  // The custom range's first click, until its last day is picked.
  const [pickedDays, setPickedDays] = useState(null);
  // Choosing "Choose dates" opens its date picker at once, instead of a box beside the range to click.
  const customCalendar = useRef(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  useEffect(() => {
    if (openCalendar && customCalendar.current) {
      customCalendar.current.show();
      setOpenCalendar(false);
    }
  }, [openCalendar, query.range]);

  // The row whose details were opened last, so the focus can go back to its button when the panel closes.
  const openedId = useRef(null);
  const wasOpen = useRef(false);
  const openDetails = (id) => {
    openedId.current = id;
    detailsRequest(id);
  };
  const detailsOpen = !!detailsRequesting || !!details;
  useEffect(() => {
    if (wasOpen.current && !detailsOpen && openedId.current) {
      document.querySelector(`button[data-audit-id="${openedId.current}"]`)?.focus();
    }
    wasOpen.current = detailsOpen;
  }, [detailsOpen]);

  // Any filter change starts again from the first page.
  const change = (patch) => setQuery({ ...query, ...patch, pageNo: 1 });

  const [search, setSearch] = useListSearch(query.search, (value) =>
    setQuery({ ...query, search: value, pageNo: 1 })
  );

  // The search box keeps its own text until the typing settles, so clearing
  // the filters empties it too; otherwise the old text would be sent again.
  const clearAll = () => {
    setSearch("");
    setPickedDays(null);
    clearFilters();
  };

  useEffect(() => {
    optionsRequest();
    // A panel left open closes when the list opens again.
    detailsClose();
  }, [optionsRequest, detailsClose]);

  useEffect(() => {
    listRequest();
  }, [query, listRequest]);

  const rangeOptions = Object.entries(AuditLogConstants.AUDIT_RANGE_LABELS).map(([value, id]) => ({
    label: t(id),
    value,
  }));
  const kindOptions = [
    { label: t("audit_all_activity"), value: ALL },
    ...Object.entries(AuditLogConstants.AUDIT_KIND_LABELS).map(([value, id]) => ({ label: t(id), value: Number(value) })),
  ];
  const areaOptions = [
    { label: t("audit_all_areas"), value: ALL },
    ...(options?.areas || []).map((area) => ({ label: area, value: area })),
  ];
  const actionOptions = [
    { label: t("audit_all_actions"), value: ALL },
    ...(options?.actions || []).map((action) => ({ label: action, value: action })),
  ];
  const customDays = query.customRange
    ? query.customRange.map((day) => (day ? new Date(day) : null))
    : null;

  const exportAs = (fileType) => exportRequest({ fileType });

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">{t("audit_logs")}</div>
          <div className="edexlms-page-actions">
            <DropdownButton
              icon="pi pi-download"
              label={t("export")}
              disabled={!!exporting}
              loading={!!exporting}
              onClick={() => exportAs("excel")}
              items={[
                { label: t("excel"), command: () => exportAs("excel") },
                { label: t("csv"), command: () => exportAs("csv") },
              ]}
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar audit-filters">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={t("audit_search_placeholder")}
              input={{ name: "search", "aria-label": t("audit_search_placeholder"), value: search, onChange: (e) => setSearch(e.target.value) }}
            />
            <Dropdown
              data-testid="audit-range"
              aria-label={t("audit_filter_range")}
              value={query.range}
              options={rangeOptions}
              valueTemplate={(option) => (
                <span>
                  <i className="pi pi-calendar audit-range-icon" aria-hidden="true" />
                  {option?.label}
                </span>
              )}
              onChange={(e) => {
                change({ range: e.value, customRange: null });
                setOpenCalendar(e.value === AuditLogConstants.AUDIT_RANGE_CUSTOM);
              }}
            />
            {query.range === AuditLogConstants.AUDIT_RANGE_CUSTOM && (
              <Calendar
                ref={customCalendar}
                data-testid="audit-custom-range"
                aria-label={t("audit_pick_dates")}
                selectionMode="range"
                readOnlyInput
                dateFormat="dd M yy"
                placeholder={t("audit_pick_dates")}
                minDate={earliestDay()}
                maxDate={new Date()}
                value={pickedDays || customDays}
                onChange={(e) => {
                  const [first, last] = e.value || [];
                  setPickedDays(e.value);
                  if (first && last) {
                    setPickedDays(null);
                    change({ customRange: [first.toISOString(), last.toISOString()] });
                  }
                }}
              />
            )}
            <Dropdown
              data-testid="audit-kind"
              aria-label={t("audit_filter_activity")}
              value={query.kind ?? ALL}
              options={kindOptions}
              onChange={(e) => change({ kind: e.value === ALL ? null : e.value })}
            />
            <Dropdown
              data-testid="audit-area"
              aria-label={t("audit_filter_area")}
              value={query.area || ALL}
              options={areaOptions}
              onChange={(e) => change({ area: e.value === ALL ? null : e.value })}
            />
            <Dropdown
              data-testid="audit-action"
              aria-label={t("audit_filter_action")}
              value={query.action || ALL}
              options={actionOptions}
              onChange={(e) => change({ action: e.value === ALL ? null : e.value })}
            />
            <AuditLogPeoplePicker
              value={query.userId}
              name={query.userName}
              onChange={(person) => change({ userId: person?.id || null, userName: person?.name || null })}
            />
          </div>
          {/* Only a load that answered with no entries is empty: not before the first answer, not a failure. */}
          {loaded && !requesting && totalCount === 0 ? (
            <div className="audit-empty">
              <p>{t("audit_no_activity")}</p>
              <Button link label={t("audit_clear_filters")} onClick={clearAll} />
            </div>
          ) : (
            <List
              pageQuery={{ pageNo: query.pageNo, pageSize: query.pageSize, sortColumn: "createdAt", sortBy: query.sortBy }}
              handlePageChange={(page) =>
                setQuery({ ...query, pageNo: page.page + 1, pageSize: page.rows })
              }
              handleOnSortChange={(sort) =>
                setQuery({ ...query, pageNo: 1, sortBy: sort.sortOrder === 1 ? "asc" : "desc" })
              }
              handleRowClick={(e) => openDetails(e.data.id)}
              loading={requesting}
              data={list || []}
              totalRecords={totalCount}
              selectionMode=""
              columns={[
                { field: "createdAt", header: "audit_when", sortable: true, body: When },
                { field: "actor", header: "audit_who", body: Who },
                { field: "action", header: "audit_action", body: Action },
                { field: "area", header: "audit_area" },
                {
                  field: "recordName",
                  header: "audit_record",
                  body: (row) =>
                    Record(
                      row,
                      openDetails,
                      intl.formatMessage({ id: "audit_view_details_of" }, { title: detailsTitle(row) }),
                      t("audit_view_details")
                    ),
                },
                { field: "summary", header: "audit_what_changed", body: WhatChanged },
              ]}
            />
          )}
          <div className="audit-kept">
            {intl.formatMessage(
              { id: "audit_kept_for_period" },
              { period: retentionPeriod(retentionDays, intl.formatMessage) }
            )}
          </div>
        </div>
      </div>
      <AuditLogDetails
        visible={detailsOpen}
        details={details}
        requesting={detailsRequesting}
        onHide={detailsClose}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  query: state?.auditLogs?.query,
  list: state?.auditLogs?.list,
  totalCount: state?.auditLogs?.totalCount,
  requesting: state?.auditLogs?.requesting,
  loaded: state?.auditLogs?.loaded,
  exporting: state?.auditLogs?.exporting,
  retentionDays: state?.global?.enterprise?.auditLogRetentionDays,
  options: state?.auditLogs?.options,
  details: state?.auditLogs?.details,
  detailsRequesting: state?.auditLogs?.detailsRequesting,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: () => dispatch(auditLogListRequest()),
  setQuery: (query) => dispatch(auditLogSetQuery(query)),
  clearFilters: () => dispatch(auditLogClearFilters()),
  optionsRequest: () => dispatch(auditLogOptionsRequest()),
  detailsRequest: (id) => dispatch(auditLogDetailsRequest(id)),
  detailsClose: () => dispatch(auditLogDetailsClose()),
  exportRequest: (payload) => dispatch(auditLogExportRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogList);
